import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Tooltip from '../Tooltip/Tooltip'
import ShareOptionsModalOption from './ShareOptionsModalOption'
import ShareOptionsModalOptionDropdown from './ShareOptionsModalOptionDropdown'
import { getSubdomainAndTLD } from '../../utils/general'
import { setShowPaymentModal, showUpgradeCardModal, showTrackedLinkCreateModal } from '../../actions'
import { PLANS } from '../../constants/plans'
import { Badge } from 'react-bootstrap'

const Share = ({ site, onQRClick, onEmbed, onClose }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const copyTargetRef = useRef(null)
  const dispatch = useDispatch()

  const url = `https://${site.subdomain}`
  const productId = useSelector((state) => state.manage.userProfile.productId) || null
  const hasAccess = useMemo(() => {
    return [PLANS.PRO.id, PLANS.PRO_U.id].includes(productId)
  }, [productId])

  useEffect(() => {
    setTimeout(() => copyUrl(), 500)
  }, [])

  const copyUrl = (e) => {
    if (e) e?.preventDefault()

    navigator.clipboard
      .writeText(url)
      .then(() => {
        setShowTooltip(true)
      })
      .catch((err) => {
        console.error('Failed to copy URL: ', err)
      })
  }

  const handleTooltipHide = () => {
    setShowTooltip(false)
  }

  const handleTrackedLinksClick = () => {
    if (!hasAccess) {
      dispatch(
        showUpgradeCardModal({
          title: 'Create tracked links',
          gif: '/assets/img/gifs/tracked-links.gif',
          desc: 'Generate a unique link for each recipient, track opens and clicks, and know exactly when your content is viewed.',
          cta: 'Create tracked link',
          onClick: () =>
            dispatch(
              setShowPaymentModal({
                showPaymentModal: true,
                upgradePlanId: PLANS.PRO.id
              })
            )
        })
      )
    } else {
      dispatch(showTrackedLinkCreateModal({ site }))
      onClose()
    }
  }

  const urlParams = getSubdomainAndTLD({ site: { subdomain: url } })
  const title = 'Check out this link!'
  const redirect_url = `${urlParams.subdomain}.tiiny.xyz/?d=${urlParams.tld.replace(/^\./, '')}`

  const linkedInURL = `https://www.linkedin.com/sharing/share-offsite/?url=${redirect_url}`
  const facebookURL = `https://www.facebook.com/sharer/sharer.php?u=${redirect_url}&quote=${encodeURIComponent(title)}`

  const socialItems = [
    {
      icon: '/assets/icons/facebook.svg',
      label: 'Facebook',
      href: facebookURL
    },
    {
      icon: '/assets/icons/linkedin.svg',
      label: 'LinkedIn',
      href: linkedInURL
    }
  ]

  return (
    <div>
      <div className="bg-white rounded px-2 py-4">
        <h1 className="text-center h3 fw-bold mb-3">Share</h1>
        <div className="text-center h4" ref={copyTargetRef} onClick={copyUrl} style={{ cursor: 'pointer' }}>
          <span className="link">{url.replace('https://', '')}</span>
          <img
            className="ms-2"
            style={{ paddingBottom: '1px', verticalAlign: 'middle' }}
            src="/assets/icons/export/copy.svg"
            alt="Copy URL"
            height="18"
            width="18"
          />
          <Tooltip
            active={showTooltip}
            time={1500}
            text="URL Copied!"
            placement="bottom"
            target={copyTargetRef.current}
            onHide={handleTooltipHide}
          />
        </div>
        <div className="d-flex flex-column mx-3 mt-3">
          <ShareOptionsModalOption
            icon="/assets/icons/export/tracked.svg"
            title={
              <span>
                Share & Track{' '}
                <Badge bg="secondary" className="ms-2 badge-bg">
                  New
                </Badge>
              </span>
            }
            description="Create a unique link for a specific recipient and track when it’s opened"
            onClick={handleTrackedLinksClick}
          />
          <ShareOptionsModalOption
            icon="/assets/icons/export/qr.svg"
            title="QR Code"
            description="Get a QR code to put anywhere online & offline"
            onClick={onQRClick}
          />
          <ShareOptionsModalOption
            icon="/assets/icons/export/embed.svg"
            title="Embed"
            description="Embed your project's code on another website"
            onClick={onEmbed}
          />
          <ShareOptionsModalOptionDropdown
            icon="/assets/icons/export/share.svg"
            title="Social Sharing"
            description="For sharing on social media, and other public platforms"
            items={socialItems}
          />
        </div>
      </div>
    </div>
  )
}

export default Share
